import React, { useEffect, useState, useMemo } from 'react'
import AdminDashboardLayout from './AdminDashboardLayout'
import { useDispatch, useSelector } from 'react-redux'
import { getUsersAPI, UpdateUserStatusAPI } from '../../redux/slices/SuperAdminUserSlice';
import { toast } from 'react-toastify';
import { errorHandler, base } from '../../constants/base';
import { Link } from 'react-router-dom';
import AdminDataTable from '../../components/AdminComponents/AdminDataTable';
import './ModuleAnalytics.css';

const SuperAdminUser = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.superAdminUserSlice.loading);
    const users = useSelector(state => state.superAdminUserSlice.users);
    const analytics = useSelector(state => state.superAdminUserSlice.analytics);
    const pagination = useSelector(state => state.superAdminUserSlice.pagination);
    const [currentPage, setCurrentPage] = useState(pagination?.current_page || 1);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchValue, setSearchValue] = useState('');

    const fetchUsers = async (page = currentPage, search = searchValue, status = statusFilter) => {
        const payload = {
            page: page,
            search: search,
            status: status
        }
        await dispatch(getUsersAPI(payload));
    }

    useEffect(() => {
        fetchUsers(currentPage, searchValue, statusFilter);
    }, [currentPage, statusFilter]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleSearch = (value) => {
        setSearchValue(value);
        setCurrentPage(1);
        fetchUsers(1, value, statusFilter);
    }

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    }

    const handleChangeStatus = async (id, newStatus) => {
        const statusUpdate = {
            id: id,
            status: newStatus,
        }

        const res = await dispatch(UpdateUserStatusAPI(statusUpdate))

        if (res.type === 'UpdateUserStatusAPI/fulfilled') {
            if (res.payload.success || res.payload.status || res.payload.data.status === 200) {
                fetchUsers(currentPage, searchValue, statusFilter);
                toast.success("User status updated successfully!");
            } else {
                errorHandler(res.payload.errors);
            }
        } else if (res.type === 'UpdateUserStatusAPI/rejected') {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    const analyticsCards = [
        { title: 'Total Users', value: analytics?.total || 0, icon: 'fa-solid fa-users', color: '#EF4444' },
        { title: 'Active', value: analytics?.active || 0, icon: 'fa-solid fa-user-check', color: '#60A5FA' },
        { title: 'Pending', value: analytics?.pending || 0, icon: 'fa-solid fa-user-clock', color: '#4ADE80' },
        { title: 'Blocked', value: analytics?.blocked || 0, icon: 'fa-solid fa-user-xmark', color: '#FBBF24' },
    ];

    // Define columns for DataTable
    const columns = useMemo(() => [
        {
            Header: 'User',
            accessor: 'name',
            Cell: ({ row }) => (
                <div className="admin-dt-user-cell">
                    {row.original?.images?.[0]?.path ? (
                        <img
                            src={`${base.imageUrl}${row.original.images[0].path}`}
                            alt={row.original.name}
                            className="admin-dt-user-avatar"
                        />
                    ) : (
                        <div className="admin-dt-user-placeholder">
                            <i className="fas fa-user"></i>
                        </div>
                    )}
                    <div className="admin-dt-user-info">
                        <p className="admin-dt-user-name">{row.original?.name}</p>
                        <p className="admin-dt-user-email">{row.original?.email}</p>
                    </div>
                </div>
            ),
        },
        {
            Header: 'Social Security No',
            accessor: 'ss_number',
            className: 'text-center',
            Cell: ({ value }) => value || '-',
        },
        {
            Header: 'Status',
            accessor: 'status',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ row }) => (
                <select
                    value={row.original?.status}
                    onChange={(e) => handleChangeStatus(row.original?.id, e.target.value)}
                    className="admin-dt-select"
                >
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Block">Block</option>
                </select>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'id',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ value }) => (
                <div className="admin-dt-actions">
                    <Link to={`/admin/user/view/${value}`} className="admin-dt-action-btn view">
                        <i className="fas fa-eye"></i>
                    </Link>
                </div>
            ),
        },
    ], []);

    const filterElements = (
        <select value={statusFilter} onChange={handleStatusFilter} className="admin-dt-filter-select">
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
            <option value="Block">Blocked</option>
        </select>
    );

    return (
        <AdminDashboardLayout>
            {/* Analytics Cards */}
            <div className="module-analytics-grid">
                {analyticsCards.map((card, index) => (
                    <div key={index} className="module-analytics-card">
                        <div className="module-analytics-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                            <i className={card.icon}></i>
                        </div>
                        <div className="module-analytics-content">
                            <p className="module-analytics-label">{card.title}</p>
                            <h3 className="module-analytics-value">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <AdminDataTable
                columns={columns}
                data={users}
                title="Users"
                icon="fa-solid fa-users"
                loading={loading}
                searchable={true}
                searchPlaceholder="Search users..."
                onSearch={handleSearch}
                filters={filterElements}
                serverSidePagination={{
                    currentPage: pagination?.current_page || 1,
                    totalPages: pagination?.total_pages || 1,
                    total: pagination?.total || 0,
                    perPage: pagination?.per_page || 10,
                    onPageChange: handlePageChange,
                }}
            />
        </AdminDashboardLayout>
    )
}

export default SuperAdminUser
